import React, { useLayoutEffect, useRef, useState } from "react";
import { useEvent } from "@hooks/useEvent";
import { useSignal } from "@preact/signals-react";

type VirtualListItemProps = {
    viewportRef: React.RefObject<HTMLDivElement | null>;
    children?: React.ReactNode;
    defaultWidth?: number;
    defaultHeight?: number;
};
export default function VirtualListItem({ viewportRef, children, defaultWidth, defaultHeight }: VirtualListItemProps) {
    const [inViewport, setInViewport] = useState(false);
    const itemRef = useRef<HTMLDivElement | null>(null);
    const lastItemWidth$ = useSignal(defaultWidth ?? 0);
    const lastItemHeight$ = useSignal(defaultHeight ?? 0);

    const handleIntersect: IntersectionObserverCallback = useEvent((entries) => {
        const lastEntry = entries[entries.length - 1];
        if (lastEntry?.isIntersecting) {
            setInViewport(true);
        } else {
            setInViewport(false);
        }
    });

    useLayoutEffect(() => {
        if (viewportRef.current && itemRef.current) {
            const observer = new IntersectionObserver(handleIntersect, {
                root: viewportRef.current,
            });
            observer.observe(itemRef.current!);

            return () => {
                observer.disconnect();
            };
        }
        return undefined;
    }, [viewportRef.current, itemRef.current]);

    const handleItemResize: ResizeObserverCallback = useEvent((entries) => {
        const entry = entries[0];
        if (entry?.contentRect) {
            lastItemWidth$.value = entry.contentRect.width;
            lastItemHeight$.value = entry.contentRect.height;
        }
    });

    useLayoutEffect(() => {
        if (inViewport && itemRef.current) {
            const observer = new ResizeObserver(handleItemResize);
            observer.observe(itemRef.current!);

            return () => {
                observer.disconnect();
            };
        }
        return undefined;
    }, [itemRef.current]);

    return (
        <div ref={itemRef}>
            {inViewport ? children : <div style={{ width: lastItemWidth$.value, height: lastItemHeight$.value }} />}
        </div>
    );
}
